import { Check, Copy, ExternalLink, MailCheck, RefreshCw, RotateCcw, Stamp } from "lucide-react";
import { useEffect, useState } from "react";

import { FilePreviewDialog } from "@/components/files/file-preview-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useGenerateStampedExhibits, useMarkExhibitsTransmitted } from "../../api/use-claim-actions";
import { ExhibitsList } from "../exhibits/exhibits-list";
import { useClaimExhibits } from "../exhibits/use-claim-exhibits";

/** Port of states-cta/GenerateStampedExhibitsCTA.vue — pièces tamponnées + transmission. */
export function GenerateStampedExhibitsCTA({ claim }: { claim: ClaimWithRelations }) {
  const [showExhibits, setShowExhibits] = useState(false);
  const [confirmTransmission, setConfirmTransmission] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    exhibits,
    exhibitData,
    isFetching: isFetchingExhibits,
    isReordered,
    moveExhibit,
    removeExhibit,
    resetOrder,
    previewedFile,
    previewDocument,
    clearPreview,
  } = useClaimExhibits(claim.id);

  const generateMutation = useGenerateStampedExhibits(claim.id);
  const transmitMutation = useMarkExhibitsTransmitted(claim.id);

  // Check if stamped exhibits already generated
  const stampedExhibitsLink = claim.procedure?.stampedExhibitsShareableLink;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the local flow when switching claim
  useEffect(() => {
    setShowExhibits(false);
    setConfirmTransmission(false);
  }, [claim.id]);

  function copyLink() {
    if (stampedExhibitsLink) {
      void navigator.clipboard.writeText(stampedExhibitsLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  async function generateStampedExhibits() {
    // Prepare ordered exhibits to send to backend
    const orderedExhibits = exhibits.map((doc) => ({
      boxFileId: doc.boxFileId,
      name: doc.name,
      exhibitNumber: doc.exhibitNumber,
      ...(doc.exhibitSubNumber !== undefined && { exhibitSubNumber: doc.exhibitSubNumber }),
    }));

    const { shareableLink } = await generateMutation.mutateAsync({ orderedExhibits });
    window.open(shareableLink, "_blank");
  }

  function markAsTransmitted() {
    if (!confirmTransmission) {
      setConfirmTransmission(true);
      return;
    }
    transmitMutation.mutate(undefined, {
      onSettled: () => setConfirmTransmission(false),
    });
  }

  const exhibitsListBlock = isFetchingExhibits ? (
    <div className="flex items-center justify-center py-8">
      <Spinner className="size-6 text-gray-400" />
    </div>
  ) : (
    <>
      <ExhibitsList
        exhibitData={exhibitData}
        onPreview={previewDocument}
        reorder={{
          onMoveUp: (id) => moveExhibit(id, "up"),
          onMoveDown: (id) => moveExhibit(id, "down"),
          onRemove: removeExhibit,
        }}
      />
      {isReordered ? (
        <div className="mt-2 text-right">
          <Button variant="ghost" size="sm" onClick={resetOrder}>
            <RotateCcw />
            Réinitialiser l'ordre
          </Button>
        </div>
      ) : null}
    </>
  );

  return (
    <>
      <FilePreviewDialog open={Boolean(previewedFile)} file={previewedFile} onClose={clearPreview} />

      <div className="border-b bg-slate-50">
        <div className="mx-auto max-w-xl py-5 text-left">
          <div className="rounded-md border bg-white p-6 shadow">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-purple-100 p-2">
                <Stamp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-base leading-6 font-semibold text-gray-900">
                  Générer les pièces tamponnées
                </h2>
                <p className="text-sm text-gray-500">Communication des pièces à la partie adverse</p>
              </div>
            </div>

            {stampedExhibitsLink ? (
              <>
                {/* Already generated - show link */}
                <div className="rounded-md border border-green-200 bg-green-50 p-4">
                  <p className="mb-3 text-sm text-green-700">Les pièces tamponnées ont déjà été générées.</p>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <img
                        src="https://ucarecdn.com/2ab572b5-0498-44b8-9eef-76c2a9e4cb0e/789931495607_cc585a2ba3a192a9c371_512.png"
                        alt="Box"
                        className="absolute top-1/2 left-2.5 h-5 w-5 -translate-y-1/2 rounded"
                      />
                      <input
                        type="text"
                        value={stampedExhibitsLink}
                        readOnly
                        className="w-full rounded-md border bg-white py-2 pr-3 pl-10 text-sm text-gray-600 ring-1 ring-gray-300 ring-offset-1 focus:ring-blue-400"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={copyLink}
                      className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      title={copied ? "Copié !" : "Copier le lien"}
                    >
                      {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowExhibits((value) => !value)}
                      className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      title={showExhibits ? "Masquer les pièces" : "Voir les pièces"}
                    >
                      <RefreshCw className="h-5 w-5" />
                    </button>
                    <a
                      href={stampedExhibitsLink}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      title="Ouvrir dans Box"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  </div>
                </div>

                {showExhibits ? (
                  <div className="mt-4">
                    {exhibitsListBlock}
                    <div className="mt-4 text-center">
                      <Button
                        onClick={() => void generateStampedExhibits()}
                        disabled={generateMutation.isPending}
                      >
                        <Stamp />
                        Régénérer les pièces tamponnées
                      </Button>
                    </div>
                  </div>
                ) : null}

                {/* Mark as transmitted button */}
                <div className="mt-4 ml-4 text-left">
                  <Button
                    variant={confirmTransmission ? "destructive" : "default"}
                    onClick={markAsTransmitted}
                    disabled={transmitMutation.isPending}
                  >
                    {confirmTransmission ? <Check /> : <MailCheck />}
                    {confirmTransmission
                      ? "Confirmer la transmission"
                      : "Pièces communiquées à la partie adverse"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Not generated yet */}
                {exhibitsListBlock}
                <div className="mt-4 text-center">
                  <Button
                    onClick={() => void generateStampedExhibits()}
                    disabled={
                      generateMutation.isPending || exhibitData.toStamp.length === 0 || isFetchingExhibits
                    }
                  >
                    <Stamp />
                    Générer les pièces tamponnées
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
